// --- React ---
import React from 'react';

// --- 공통 UI 컴포넌트 ---
import {
  Text,
  TextButton,
  Tag,
  IconStable,
  IconWarning,
  IconAlert,
  IconCalib,
  DayTag,
} from '@/components/ui';

// --- 타입 ---
import type { MonitoringDataItem } from './monitoringDeviceTypes';
import {
  LITE_STATUS_GROUP_MAP,
  MINI_STATUS_GROUP_MAP,
  getHighestPriorityGroup,
  SystemStatusGroup,
  SYSTEM_STATUS_GROUP_DISPLAY_LABEL,
} from '@packages/types/device/common/system_status_group';

// --- 유틸 ---
import { formatDateTime, getBatteryIcon } from './monitoringDeviceUtils';
import { isDueIntervalDisabled } from '@/utils/date';
import { getMonitoringGroupColors } from '@/utils/monitoringGroupColors';
import { getDeviceDisplayName } from '@packages/types/app';
import { LITE_IMAGE_WHOLE } from '@/utils/deviceImageUtils';

// --- 스타일 · 테마 ---
import { StyledTr, StyledTd } from './MonitoringDeviceList.styles';
import { useTheme } from '@/theme';

// --- 페이지 컴포넌트 ---
import { DeviceExpandedDetail } from './DeviceExpandedDetail';

// ==================== 타입 정의 ====================

interface DeviceTableRowProps {
  item: MonitoringDataItem;
  isExpanded: boolean;
  onToggle: (serialNumber: string) => void;
  isAdmin?: boolean;
  onForceDisconnect?: (serialNumber: string) => void;
}

// ==================== 컴포넌트 ====================

/**
 * 모니터링 디바이스 테이블의 한 행.
 * 디바이스 기본 정보 + 클릭 시 확장 상세(DeviceExpandedDetail) 표시.
 */
export const DeviceTableRow: React.FC<DeviceTableRowProps> = React.memo(
  ({ item, isExpanded, onToggle, isAdmin, onForceDisconnect }) => {
    const { theme, themeMode } = useTheme();

    // ==================== 파생 데이터 ====================

    // 고정 GPS 우선, 없으면 모니터링 GPS 사용
    const displayLatitude =
      item.device.fixed_latitude !== null && item.device.fixed_latitude !== undefined
        ? item.device.fixed_latitude
        : item.monitoring.latitude;
    const displayLongitude =
      item.device.fixed_longitude !== null && item.device.fixed_longitude !== undefined
        ? item.device.fixed_longitude
        : item.monitoring.longitude;

    const hasGPSData =
      displayLatitude !== null &&
      displayLatitude !== undefined &&
      displayLongitude !== null &&
      displayLongitude !== undefined &&
      !isNaN(Number(displayLatitude)) &&
      !isNaN(Number(displayLongitude));

    // System Status 그룹 계산
    const systemStatusLabels = item.monitoring.system_status_name || [];

    const statusGroupMap =
      item.device_type === 'bellmini' ? MINI_STATUS_GROUP_MAP : LITE_STATUS_GROUP_MAP;
    const systemStatusGroup = getHighestPriorityGroup(systemStatusLabels, statusGroupMap);
    const systemStatusName = SYSTEM_STATUS_GROUP_DISPLAY_LABEL[systemStatusGroup];
    const groupColors = getMonitoringGroupColors(systemStatusGroup, theme.colors);

    // 교정/범프 인터벌 계산
    const calIntervalRaw =
      (item.configuration?.calibration_interval as number[] | number | undefined | null) ??
      item.device.calibration_interval;
    const bumpIntervalRaw =
      (item.configuration?.bump_interval as number[] | number | undefined | null) ??
      item.device.bump_interval;

    const toIntervalArray = (v: number | number[] | null | undefined): number[] | undefined => {
      if (v == null) return undefined;
      return Array.isArray(v) ? v : [v];
    };

    const lastConnectTime = formatDateTime(item.device.last_connect);
    const calDisabled = isDueIntervalDisabled(toIntervalArray(calIntervalRaw));
    const bumpDisabled = isDueIntervalDisabled(toIntervalArray(bumpIntervalRaw));
    const calDays = item.device.cal_due_day_num;
    const bumpDays = item.device.bump_due_day_num;
    const site = item.device.site;

    // ==================== 렌더링 ====================

    return (
      <React.Fragment>
        <StyledTr
          isExpanded={isExpanded}
          isConnected={item.device.is_connected}
          onClick={() => onToggle(item.device.serial_number)}
        >
          {/* 디바이스 이미지 + 시리얼 번호 */}
          <StyledTd>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '6px',
                padding: '4px',
              }}
            >
              <img
                src={item.deviceImageUrl || LITE_IMAGE_WHOLE}
                alt={getDeviceDisplayName(item.device_type, item.device.app_id)}
                style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                onError={e => {
                  const img = e.currentTarget;
                  img.onerror = null;
                  img.style.visibility = 'hidden';
                }}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                }}
              >
                <Text weight="semibold">{item.device.serial_number}</Text>
                <Text size="sm" weight="regular">
                  {item.information?.fw_version || '-'}
                </Text>
              </div>
            </div>
          </StyledTd>

          {/* 사이트명 */}
          <StyledTd>
            <Text size="md" weight="medium">
              {site?.name || '-'}
            </Text>
          </StyledTd>

          {/* 위치 */}
          <StyledTd>
            <Text size="md" weight="medium">
              {item.device.last_location || '-'}
            </Text>
          </StyledTd>

          {/* 작업자명 */}
          <StyledTd>
            <Text size="md" weight="medium">
              {item.device.name || '-'}
            </Text>
          </StyledTd>

          {/* 앱 연결 상태 아이콘 */}
          <StyledTd>
            <img
              src="/icons/connect2.svg"
              alt="Connection"
              style={{
                width: '20px',
                height: '20px',
                filter: item.device.is_connected
                  ? 'brightness(0) saturate(100%) invert(44%) sepia(99%) saturate(1755%) hue-rotate(101deg) brightness(92%) contrast(95%)'
                  : 'brightness(0) saturate(100%) invert(51%) sepia(0%) saturate(0%) hue-rotate(173deg) brightness(93%) contrast(89%)',
              }}
            />
          </StyledTd>

          {/* GPS 상태 아이콘 */}
          <StyledTd>
            <img
              src={
                item.device.is_connected && hasGPSData ? '/icons/gps-on.svg' : '/icons/gps-off.svg'
              }
              alt="GPS"
              style={{
                width: '20px',
                height: '20px',
                filter:
                  item.device.is_connected && hasGPSData
                    ? 'brightness(0) saturate(100%) invert(44%) sepia(99%) saturate(1755%) hue-rotate(101deg) brightness(92%) contrast(95%)'
                    : 'brightness(0) saturate(100%) invert(51%) sepia(0%) saturate(0%) hue-rotate(173deg) brightness(93%) contrast(89%)',
              }}
            />
          </StyledTd>

          {/* 시스템 상태 태그 */}
          <StyledTd>
            {!item.device.is_connected ? (
              // 미연결 기기 (리스트에 남은 기기는 모두 1시간 이내): 회색 Disconnected 태그
              <Tag
                variant="custom"
                size="md"
                style={{ width: '100%', minWidth: '80px', maxWidth: '120px' }}
                backgroundColor={theme.colors.surface.interactive.hover}
                color={theme.colors.text.secondary}
                icon={<IconStable />}
              >
                Disconnected
              </Tag>
            ) : (
              <Tag
                variant="custom"
                size="md"
                style={{ width: '100%', minWidth: '80px', maxWidth: '120px' }}
                backgroundColor={groupColors.lightColor}
                color={groupColors.color}
                icon={
                  systemStatusGroup === SystemStatusGroup.GroupA ? (
                    <IconStable />
                  ) : systemStatusGroup === SystemStatusGroup.GroupC ? (
                    <IconCalib />
                  ) : systemStatusGroup === SystemStatusGroup.GroupB ? (
                    <IconWarning />
                  ) : systemStatusGroup === SystemStatusGroup.GroupD ? (
                    <IconAlert />
                  ) : (
                    <IconStable />
                  )
                }
              >
                {systemStatusName}
              </Tag>
            )}
          </StyledTd>

          {/* 온도 */}
          <StyledTd>
            <Text size="md" weight="medium">
              {item.monitoring.temperature !== undefined && item.monitoring.temperature !== null
                ? `${Number(item.monitoring.temperature).toFixed(1)}`
                : '-'}
            </Text>
          </StyledTd>

          {/* 배터리 상태 */}
          <StyledTd>
            {item.monitoring.battery_state !== undefined &&
            item.monitoring.battery_state !== null ? (
              <img
                src={getBatteryIcon(item.monitoring.battery_state)}
                alt="Battery"
                style={{
                  width: '32px',
                  height: '32px',
                  filter: themeMode === 'dark' ? 'invert(1)' : 'none',
                }}
              />
            ) : (
              <Text size="md">-</Text>
            )}
          </StyledTd>

          {/* 교정 만기일 */}
          <StyledTd>
            {calDisabled || calDays === null || calDays === undefined ? (
              <Text size="xs">-</Text>
            ) : (
              <DayTag days={calDays} size="l" />
            )}
          </StyledTd>

          {/* 범프 테스트 만기일 */}
          <StyledTd>
            {bumpDisabled || bumpDays === null || bumpDays === undefined ? (
              <Text size="xs">-</Text>
            ) : (
              <DayTag days={bumpDays} size="l" />
            )}
          </StyledTd>

          {/* 마지막 접속 시간 + 상세 토글 */}
          <StyledTd>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Text size="sm" weight="regular" style={{ whiteSpace: 'nowrap' }}>
                {lastConnectTime}
              </Text>
              <TextButton
                data-tour="monitoring-device-detail-btn"
                onClick={e => {
                  e.stopPropagation();
                  onToggle(item.device.serial_number);
                }}
              >
                {isExpanded ? 'Hide details' : 'Show Details'}
              </TextButton>
              {isAdmin && item.device.is_connected && (
                <TextButton
                  onClick={e => {
                    e.stopPropagation();
                    onForceDisconnect?.(item.device.serial_number);
                  }}
                  style={{ color: theme.colors.status.danger.stroke.text }}
                >
                  Disconnect
                </TextButton>
              )}
            </div>
          </StyledTd>
        </StyledTr>

        {/* 확장 상세 패널 */}
        {isExpanded && (
          <tr>
            <td colSpan={12} style={{ padding: 0 }}>
              <DeviceExpandedDetail item={item} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }
);
